import { Box, Grid, Paper, Typography, useTheme, Button, FormControl, Select, MenuItem, Skeleton } from "@mui/material";
import { FaBox, FaBuilding, FaChartBar, FaHistory, FaProjectDiagram, FaQrcode, FaRecycle } from "react-icons/fa";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuFactory, LuTruck, LuZap, LuFootprints, LuBox, LuInfo } from "react-icons/lu";
import { MdOutlineSync } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import { FiCopy, FiLayers, FiTrash2, FiCheckCircle, FiShare2, FiZap } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import ProductCard from "./components/ProductCard";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";

import { useAuth } from "../../authentication/AuthProvider";
import AddButton from "../../components/button/AddButton";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import HideButton from "../../components/button/HideButton";
import RefreshButton from "../../components/button/RefreshButton";
import SaveButton from "../../components/button/SaveButton";
import UnhideButton from "../../components/button/UnhideButton";
import DashboardGlobalFootprint from "../../components/chart/DashboardGlobalFootprint";
import { MainContainer } from "../../components/container/MainContainer";
import ResizableBox from "../../components/container/ResizableBox";
import StandardCardContainer from "../../components/container/StandardCardContainer";
import { TableFooterCheckBoxesContainer } from "../../components/container/TableFooterCheckBoxesContainer";
import { TableFooterContainer } from "../../components/container/TableFooterContainer";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import ContextMenu from "../../components/context-menu/ContextMenu";
import DialogDelete from "../../components/dialog/DialogDelete";
import UnhideDialog from "../../components/dialog/UnhideDialog";
import { envfactor_types } from "../../components/Envfactor_Types";
import { PRODUCT_TYPES, getCategoriesForType, getSubCategoriesForType } from "../../data/lciMaterialTypes";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid, { GridPagination } from "../../components/grid/AgGrid";
import {
  centerAlignColDef,
  checkBoxColDef,
  defaultColDef,
  exactFloatFormater,
  floatFormater,
  leftAlignColDef,
  rightAlignColDef,
} from "../../components/grid/columnDefinitions/columnDefinitions";
import { ToggleAll } from "../../components/grid/headers/ToggleAll";
import CheckBox from "../../components/input/CheckBox";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Product } from "../../db_access/product";
import {
  useProductsDashboard,
  useHiddenProducts,
  useProductsGwpByStage,
  useProductsGwpByEnvType,
  useInvalidateProductCache
} from "../../utilities/useProductQueries";
import "../../style/pages-style/Supplier.css";
import "../../style/pages-style/products/ProductDashboard.css";
import "../../style/pages-style/Organization/organizationDashboard.css";
import routing from "../../routing";
import CardTotalGwp from "./Dashboard/CardTotalGwp";

import CardTotalGwpStage from "./Dashboard/CardTotalGwpStage";

import MetricCard from "../../app_components/MetricCard";
import LifecycleStageCards from "../../app_components/LifecycleStageCards";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const METRIC_CONFIG = [
  { key: "total", title: "Total Carbon Footprint", icon: LuFootprints, accent: "#16a34a", bg: "rgba(22, 163, 74, 0.12)", unit: "t CO₂e" },
  { key: "material", title: "Material Impact", icon: LuBox, accent: "#2563eb", bg: "rgba(37, 99, 235, 0.12)", unit: "t CO₂e" },
  { key: "transport", title: "Transport Emissions", icon: LuTruck, accent: "#d97706", bg: "rgba(217, 119, 6, 0.12)", unit: "t CO₂e" },
  { key: "energy", title: "Energy Impact", icon: LuZap, accent: "#7c3aed", bg: "rgba(124, 58, 237, 0.12)", unit: "t CO₂e" },
];

const STAGE_CONFIG = [
  { key: "a1a3", title: "A1 - A3", subtitle: "Manufacture", icon: LuFactory, accent: "#0891b2", bg: "rgba(8, 145, 178, 0.1)", unit: "t CO₂e" },
  { key: "reused", title: "Reused GWP", subtitle: "Circular Carbon", icon: MdOutlineSync, accent: "#10b981", bg: "rgba(16, 185, 129, 0.1)", unit: "t CO₂e" },
  { key: "mci", title: "Material Circularity Indicator", subtitle: "Circularity Index", icon: FaRecycle, accent: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)", unit: "Index" },
];

const PRODUCT_DEFAULT_COLUMNS = [
  "name",
  "supplier",
  "year",
  "type",
  "category",
  "sub_category",
  "gwp",
  "total_mass",
  "biogenic_gwp",
  "cost_per_gwp",
  "mci",
  "gwp_reuse_vs_virgin",
];

const ProductDashboard = () => {
  const {
    setSelectedProducts,
    selectedProductIds,
    setSelectedProductIds,
    refresh,
    setRefresh,
    columnVisibilities,
    isSaveClicked,
    setIsSaveVisible,
    setIsLoading,
    setIsSaveClicked,
    envFactorOptions,
    setIsSaveError,
    enqueueAlert,
  } = useContextProvider();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isAdmin } = useAuth();
  const colVisibility_key = "product_dashboard_table_column_visibility";
  const defaultNewProduct = { name: "", supplier: "", type: "", category: "", sub_category: "", is_published_template: false, year: new Date().getFullYear() };
  const gridRef = useRef(null);
  const gridHeaderContainerRef = useRef(null);
  const dialogRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [focusedProduct, setFocusedProduct] = useState();
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [searchText, setSearchText] = useState("");
  const [dashboardView, setDashboardView] = useState("Grid View"); // "Grid View" or "Card View"
  const [activeSubTab, setActiveSubTab] = useState("Dashboard");
  const [templateView, setTemplateView] = useState("Card View"); // "Card View" or "Grid View"
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [validationSubTab, setValidationSubTab] = useState("Pending"); // "Pending" or "Validated"
  const [validationView, setValidationView] = useState("Grid View"); // "Grid View" or "Card View"
  const [productImageCache, setProductImageCache] = useState({});
  const productImageCacheRef = useRef({});

  const resolveProductImageFromRow = useCallback((product) => {
    const candidates = [
      product?.material_photo,
      product?.photo_url,
      product?.photo,
      product?.image_url,
      product?.image,
      product?.thumbnail,
      product?.product_photo,
      product?.workspace_photo,
      product?.workspace_photo_url,
    ];

    for (const value of candidates) {
      if (!value) continue;
      if (typeof value === "string" && value.trim()) return value;
      if (typeof value === "object") {
        if (typeof value.url === "string" && value.url.trim()) return value.url;
        if (typeof value.secure_url === "string" && value.secure_url.trim()) return value.secure_url;
        if (typeof value.path === "string" && value.path.trim()) return value.path;
      }
    }

    return "";
  }, []);

  const handleCardMenuClick = (event, product) => {
    event.preventDefault();
    event.stopPropagation();
    setLocalSelectedIds([product.id]);
    setSelectedProducts([product]);
    setSelectedProductIds([product.id]);
    setMenuPosition({ x: event.clientX, y: event.clientY });
    setMenuVisible(true);
  };

  const openProductWorkspace = useCallback((product) => {
    if (!product?.id) return;
    setLocalSelectedIds([product.id]);
    setSelectedProducts([{ ...product }]);
    setSelectedProductIds([product.id]);
    navigate(`${routing.sector.products.workspace}?id=${product.id}`);
  }, [navigate, setSelectedProducts, setSelectedProductIds]);

  const [globalView, setGlobalView] = useState(false);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [actionQueue, setActionQueue] = useState([]);
  const [hiddenProducts, setHiddenProducts] = useState([]);
  const [unhideDialogOpen, setUnhideDialogOpen] = useState(false);
  const [gwpByStage, setGwpByStage] = useState([]);
  const [gwpByEnvType, setGwpByEnvType] = useState([]);

  useEffect(() => {
    productImageCacheRef.current = productImageCache;
  }, [productImageCache]);

  useEffect(() => {
    return () => {
      for (const url of Object.values(productImageCacheRef.current)) {
        if (typeof url === "string" && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      }
    };
  }, []);

  // CACHED: Fetch dashboard products
  const { data: cachedProducts, refetch: refetchProducts } = useProductsDashboard();
  const { data: cachedHiddenProducts = [] } = useHiddenProducts();
  const { invalidateDashboard } = useInvalidateProductCache();

  // Product IDs for aggregation (exclude unsaved/temporary ones to prevent focus-stealing background refetches)
  const productIdsForAggregation = useMemo(() => {
    const unsavedIds = new Set(
      actionQueue.filter((a) => a.action === "new").map((a) => a.item.id)
    );
    return products.filter((p) => !unsavedIds.has(p.id)).map((p) => p.id);
  }, [products, actionQueue]);

  // CACHED: Fetch aggregated GWP data
  const { data: cachedGwpByStage = [] } = useProductsGwpByStage(productIdsForAggregation);
  const { data: cachedGwpByEnvType = [] } = useProductsGwpByEnvType(productIdsForAggregation);

  // Use LOCAL state for selection - this prevents context re-renders from breaking editing
  const [localSelectedIds, setLocalSelectedIds] = useState([]);

  // Derive selected products from products and local IDs
  const derivedSelectedProducts = useMemo(() => {
    if (localSelectedIds.length === 0) return [];
    return products.filter((p) => localSelectedIds.includes(p.id));
  }, [products, localSelectedIds]);

  // Sync cached products to local state (for editing)
  useEffect(() => {
    if (!cachedProducts) return;
    if (actionQueue.length > 0 || isSaveClicked) {
      console.log('[ProductDashboard] ⏸️ Cache update ignored — save in progress or changes in queue:', { queue: actionQueue.length, saving: isSaveClicked });
      return;
    }
    console.log('[ProductDashboard] 📡 Data loaded into dashboard, count:', cachedProducts.length);
    if (cachedProducts.length > 0) {
      const testProd = cachedProducts.find(p => p.name?.toLowerCase().includes('testcircularity'));
      if (testProd) {
        console.log('[ProductDashboard] 🎯 TestCircularity Scan:', { 
          id: testProd.id, 
          mci: testProd.mci, 
          product_mci: testProd.product_mci,
          mci_percentage: testProd.mci_percentage,
          percentage_mci: testProd.percentage_mci,
          reused_gwp: testProd.reused_gwp 
        });
      } else {
        console.log('[ProductDashboard] 🔍 Sample MCI from first product:', cachedProducts[0].mci);
      }
    }
    setProducts((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(cachedProducts)) return prev;
      return cachedProducts;
    });
    setIsLoading(false);
  }, [cachedProducts, actionQueue.length]);

  // Sync hidden products
  useEffect(() => {
    if (cachedHiddenProducts) {
      setHiddenProducts(cachedHiddenProducts);
    }
  }, [cachedHiddenProducts]);

  useEffect(() => {
    setSelectedTemplateId(null);
  }, [activeSubTab]);

  useEffect(() => {
    let cancelled = false;
    const fetchTemplates = async () => {
      try {
        const res = await Product.get_from_admin();
        if (res.ok && !cancelled) {
          const data = await res.json();
          setTemplates(data || []);
        }
      } catch (err) {
        console.error("Error fetching product templates:", err);
      }
    };
    fetchTemplates();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const selectedTemplate = useMemo(() => {
    if (activeSubTab !== "Template Library" || !selectedTemplateId) return null;
    return templates.find((t) => t.id === selectedTemplateId);
  }, [selectedTemplateId, activeSubTab, templates]);

  const handleUseTemplate = async (template) => {
    try {
      setIsLoading(true);
      const res = await Product.duplicate([template.id]);
      if (res.ok) {
        enqueueAlert({ message: t("Product template created in Dashboard"), severity: "success" });
        invalidateDashboard();
        setRefresh((r) => !r);
        setActiveSubTab("Dashboard");
      } else {
        enqueueAlert({ message: t("Failed to duplicate template"), severity: "error" });
      }
    } catch (e) {
      console.error("Error duplicating product template:", e);
      enqueueAlert({ message: t("Failed to duplicate template"), severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const validIds = new Set([...products.map((p) => p.id), ...templates.map((t) => t.id)]);
    setProductImageCache((prev) => {
      let changed = false;
      const next = {};

      for (const [id, url] of Object.entries(prev)) {
        if (validIds.has(id)) {
          next[id] = url;
        } else {
          changed = true;
          if (typeof url === "string" && url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        }
      }

      return changed ? next : prev;
    });
  }, [products, templates]);

  useEffect(() => {
    let cancelled = false;

    const loadProductImages = async () => {
      const isCardViewActive =
        (activeSubTab === "Dashboard" && dashboardView === "Card View") ||
        (activeSubTab === "Template Library" && templateView === "Card View") ||
        (activeSubTab === "Validations" && validationView === "Card View");

      if (!isCardViewActive) return;

      const itemsToLoad = activeSubTab === "Template Library" ? templates : products;

      for (const product of itemsToLoad) {
        if (!product?.id) continue;
        if (productImageCacheRef.current[product.id]) continue;

        const mappedImage = resolveProductImageFromRow(product);
        if (mappedImage) {
          if (!cancelled) {
            setProductImageCache((prev) => (prev[product.id] ? prev : { ...prev, [product.id]: mappedImage }));
          }
          continue;
        }

        try {
          const res = await Product.get_photo(product.id);
          if (!res.ok) continue;

          const contentType = res.headers.get("content-type") || "";
          if (contentType.includes("application/json")) continue;

          const blob = await res.blob();
          if (!blob || blob.size === 0) continue;

          const objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }

          setProductImageCache((prev) => {
            if (prev[product.id]) {
              URL.revokeObjectURL(objectUrl);
              return prev;
            }
            return { ...prev, [product.id]: objectUrl };
          });
        } catch (error) {
          console.warn("[ProductDashboard] Failed to load product image", product.id, error);
        }
      }
    };

    loadProductImages();

    return () => {
      cancelled = true;
    };
  }, [products, templates, activeSubTab, dashboardView, templateView, validationView, resolveProductImageFromRow]);

  // Keep selectedProducts in sync with updated products list
  useEffect(() => {
    if (derivedSelectedProducts.length > 0 && products.length > 0) {
      const updatedSelected = derivedSelectedProducts.map((selected) => {
        const found = products.find((p) => p.id === selected.id);
        return found ? { ...found } : selected;
      });
      if (JSON.stringify(updatedSelected) !== JSON.stringify(derivedSelectedProducts)) {
        setSelectedProducts(updatedSelected);
      }
    }
  }, [products, derivedSelectedProducts, setSelectedProducts]);

  const deleteProduct = (selectedProducts) => {
    const ids = selectedProducts.map((s) => s.id);
    console.log('[ProductDashboard] 🗑️ DELETE triggered, ids:', ids);
    setActionQueue((q) => [...q, { action: "delete", ids }]);
    setProducts((p) => p.filter((prod) => !ids.includes(prod.id)));
    setIsSaveVisible(true);
    enqueueAlert({ message: "Product deleted successfully" });
    console.log('[ProductDashboard] ✅ setIsSaveVisible(true) called after DELETE');
  };
  const newProduct = () => {
    const newP = { ...defaultNewProduct, id: uuidv4() };
    console.log('[ProductDashboard] ➕ NEW product triggered, id:', newP.id);
    setProducts((p) => [...p, newP]);
    setActionQueue((q) => [...q, { action: "new", item: newP }]);
    setFocusedProduct(newP);
    setIsSaveVisible(true);
    enqueueAlert({ message: "Product created successfully" });
    console.log('[ProductDashboard] ✅ setIsSaveVisible(true) called after NEW');
  };
  const archiveProduct = (selectedProducts) => {
    const ids = selectedProducts.map((s) => s.id);
    setActionQueue((q) => [...q, { action: "archive", ids }]);
    setIsSaveVisible(true);
    enqueueAlert({ message: "Product archived successfully" });
  };

  const hideProducts = async (selectedProducts) => {
    const ids = selectedProducts.map((s) => s.id);
    try {
      await Product.hide(ids);
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error hiding products:", error);
    }
  };

  const unhideProducts = async (ids) => {
    try {
      await Product.unhide(ids);
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error unhiding products:", error);
    }
  };

  const collaborateProduct = (selectedProducts) => {
    const ids = selectedProducts.map((s) => s.id);
    setActionQueue((q) => [...q, { action: "collaborate", ids }]);
    setProducts((p) => p.filter((prod) => !ids.includes(prod.id)));
    setIsSaveVisible(true);
  };
  const duplicateProduct = (selectedProducts) => {
    const ids = selectedProducts.map((s) => s.id);
    setActionQueue((q) => [...q, { action: "duplicate", ids }]);
    setIsSaveVisible(true);
    enqueueAlert({ message: "Product duplicated successfully" });
  };
  const requestValidation = async (selectedProducts) => {
    const ids = selectedProducts.map((s) => s.id);
    try {
      await Product.request_validation(ids);
      enqueueAlert({ message: "Validation requested successfully", severity: "success" });
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error requesting validation:", error);
      enqueueAlert({ message: "Failed to request validation", severity: "error" });
    }
  };
  const openDPP = (selectedProducts) => {
    if (selectedProducts.length === 1) {
      enqueueAlert({ message: "Opening Digital Product Passport" });
      navigate(routing.sector.products.digital_passport);
    }
  };

  const toggleAllPublishedTemplates = useCallback(
    (checked) => {
      setProducts((rows) => rows.map((row) => ({ ...row, is_published_template: checked })));
      setActionQueue((q) => [...q, { action: "update_bulk", items: products.map((p) => ({ ...p, is_published_template: checked })) }]);
      setIsSaveVisible(true);
    },
    [products, setIsSaveVisible, setActionQueue, setProducts]
  );
  const onSelectionChanged = useCallback(
    (event) => {
      const selected = event.api.getSelectedRows();
      // Update local state for grid
      setLocalSelectedIds(selected.map((s) => s.id));
      // Update context for workspace navigation
      setSelectedProducts(selected.map((s) => ({ ...s })));
      setSelectedProductIds(selected.map((s) => s.id));
    },
    [setSelectedProducts, setSelectedProductIds]
  );

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    const container = gridRef.current?.eGridDiv || gridHeaderContainerRef.current?.closest('.MuiPaper-root');
    const xPos = container ? container.getBoundingClientRect().left : e.clientX;
    setMenuPosition({ x: xPos, y: e.clientY });
    setMenuVisible(true);
  }, []);
  const handleCloseMenu = () => {
    setMenuVisible(false);
  };
  const openDialog = () => {
    if (dialogRef) {
      dialogRef.current.open();
    }
  };

  const contextMenuOptions = [
    { label: t("Duplicate"), action: duplicateProduct, icon: <FiCopy />, needSelectedItems: true },
    { label: t("Intra Company"), action: archiveProduct, icon: <FiShare2 />, needSelectedItems: true },
    { label: t("Delete"), action: openDialog, icon: <FiTrash2 />, needSelectedItems: true },
    { type: 'divider' },
    { label: t("Digital Product Passport"), action: openDPP, icon: <FiZap />, needSelectedItems: true },
    { label: t("Request Validation"), action: requestValidation, icon: <FiCheckCircle />, needSelectedItems: true },
  ];

  const gwpCardProps = useMemo(() => {
    if (selectedTemplate) {
      const g = Number(selectedTemplate.gwp || 0);
      return {
        total: g,
        material: g * 0.8,
        transport: g * 0.2,
        energy: 0
      };
    }
    const ids = localSelectedIds.length > 0 ? localSelectedIds : productIdsForAggregation;
    const temp = cachedGwpByEnvType
      .filter((g) => ids.includes(g.product_id))
      .reduce(
        (acc, r) => {
          const total = Number(r.gwp_sum) || 0;
          const transport = envfactor_types.transport === r.type ? total : 0;
          const energy = envfactor_types.energy_sources === r.type ? total : 0;
          const material = total - transport - energy;
          acc.total += total;
          acc.transport += transport;
          acc.energy += energy;
          acc.material += material;
          return acc;
        },
        { total: 0, transport: 0, energy: 0, material: 0 }
      );
    return {
      total: temp.total || 0,
      material: temp.material || 0,
      transport: temp.transport || 0,
      energy: temp.energy || 0
    };
  }, [selectedTemplate, cachedGwpByEnvType, localSelectedIds, productIdsForAggregation]);

  const stageMetrics = useMemo(() => {
    if (selectedTemplate) {
      const g = Number(selectedTemplate.gwp || 0);
      return {
        a1a3: g,
        reused: Number(selectedTemplate.reused_gwp || selectedTemplate.reuse_gwp || 0),
        mci: Number(selectedTemplate.mci || selectedTemplate.product_mci || 0)
      };
    }
    const ids = localSelectedIds.length > 0 ? localSelectedIds : productIdsForAggregation;
    const selectedProds = products.filter((p) => ids.includes(p.id));

    const gwpTotal = cachedGwpByStage.filter((g) => ids.includes(g.product_id)).reduce((acc, r) => acc + (Number(r.A1_A3) || 0), 0);

    const reusedGwpTotal = selectedProds.reduce((acc, p) => acc + (Number(p.reused_gwp) || 0), 0);
    // MCI: Check every possible field name to ensure we display the synced result.
    const mciTotal = selectedProds.length > 0
      ? selectedProds.reduce((acc, p) => acc + (Number(p.mci || p.product_mci || p.mci_percentage || p.percentage_mci) || 0), 0) / selectedProds.length
      : 0;

    return {
      a1a3: gwpTotal || 0,
      reused: reusedGwpTotal || 0,
      mci: mciTotal || 0
    };
  }, [selectedTemplate, cachedGwpByStage, localSelectedIds, productIdsForAggregation, products]);

  const anyMetricReachesTons = useMemo(() => {
    return Object.values(gwpCardProps).some((val) => typeof val === "number" && val >= 1000);
  }, [gwpCardProps]);

  const anyStageReachesTons = useMemo(() => {
    return [stageMetrics.a1a3, stageMetrics.reused].some((val) => typeof val === "number" && val >= 100);
  }, [stageMetrics]);

  // Manual refresh trigger
  const lastRefreshRef = useRef(refresh);
  useEffect(() => {
    if (refresh !== lastRefreshRef.current) {
      invalidateDashboard();
      refetchProducts();
    }
    lastRefreshRef.current = refresh;
  }, [refresh, invalidateDashboard, refetchProducts]);

  useEffect(() => {
    if (!focusedProduct || !focusedProduct.id || !gridRef.current) return;
    setTimeout(() => {
      const node = gridRef.current.api.getRowNode(focusedProduct.id);
      if (node) {
        gridRef.current.api.ensureIndexVisible(node.rowIndex, "top");
        gridRef.current.api.setFocusedCell(node.rowIndex, "name");
        node.setSelected(true);
        // Automatically start editing the product name
        gridRef.current.api.startEditingCell({ rowIndex: node.rowIndex, colKey: "name" });
      }
      setFocusedProduct();
    }, 50);
  }, [products, focusedProduct]);
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  useEffect(() => {
    const save = async () => {
      try {
        console.log('[ProductDashboard] 🚀 save() called with', actionQueue.length, 'actions in queue');
        if (gridRef.current && gridRef.current.api) {
          gridRef.current.api.stopEditing();
        }
        const id_map = {};
        const processedIds = new Set();
        for (const actionItem of actionQueue) {
          const { action, item, ids, items } = actionItem;
          console.log(`[ProductDashboard] ▶️ Start ${action} at`, new Date().toISOString());
          if (action === "new") {
            const latestItem = products.find((p) => p.id === item.id) || item;
            const res = await Product.new(latestItem);
            const data = await res.json();
            id_map[item.id] = data.id;
            processedIds.add(item.id);
            // Update local state immediately with the real ID to prevent vanishing/mapping issues
            setProducts((prev) => prev.map((p) => (p.id === item.id ? { ...p, id: data.id } : p)));
          } else if (action === "update") {
            if (processedIds.has(item.id)) continue;
            const updatedItem = { ...item, id: id_map[item.id] || item.id };
            await Product.update(updatedItem);
          } else if (action === "update_bulk") {
            const updatedItems = items.map((item) => ({ ...item, id: id_map[item.id] || item.id }));
            await Product.update_bulk(updatedItems);
          } else if (action === "delete") {
            const mapped_ids = ids.map((id) => id_map[id] || id);
            await Product.delete(mapped_ids);
          } else if (action === "archive") {
            const mapped_ids = ids.map((id) => id_map[id] || id);
            await Product.archive(mapped_ids);
          } else if (action === "collaborate") {
            const mapped_ids = ids.map((id) => id_map[id] || id);
            await Product.collaborate(mapped_ids);
          } else if (action === "duplicate") {
            const mapped_ids = ids.map((id) => id_map[id] || id);
            await Product.duplicate(mapped_ids);
          }
          console.log(`[ProductDashboard] ✅ End ${action} at`, new Date().toISOString());
        }
        await invalidateDashboard();
        const { data: refreshedData } = await refetchProducts();
        
        if (refreshedData) {
          setProducts(refreshedData);
        }

        setIsLoading(false);
        setIsSaveClicked(false);
        setIsSaveVisible(false);
        setActionQueue([]);
        setRefresh((r) => !r);
        console.log('[ProductDashboard] 🎉 Save complete, queue cleared and state refreshed');
      } catch (error) {
        console.error('[ProductDashboard] ❌ Save failed:', error);
        setIsSaveError(true);
        setIsLoading(false);
        setIsSaveClicked(false);
        enqueueAlert({ message: "Failed to save changes", severity: "error" });
        setTimeout(() => setIsSaveError(false), 5000);
      }
    };
    console.log('[ProductDashboard] 🔔 isSaveClicked changed:', isSaveClicked, '| actionQueue.length:', actionQueue.length);
    if (isSaveClicked) {
      if (actionQueue.length > 0) {
        console.log('[ProductDashboard] 🚀 Calling save()');
        save();
      } else {
        console.log('[ProductDashboard] ⚠️ isSaveClicked=true but actionQueue EMPTY — resetting');
        setIsSaveClicked(false);
        setIsLoading(false);
      }
    }
  }, [isSaveClicked]);
  const colDefs = useMemo(() => {
    const isHidden = (field) => {
      const savedVis = columnVisibilities[colVisibility_key];
      if (savedVis) {
        return !savedVis.includes(field);
      }
      return !PRODUCT_DEFAULT_COLUMNS.includes(field);
    };
    const isEditable = (params) => {
      if (activeSubTab === "Template Library") return false;
      const status = params.data?.validation_status;
      return status !== "Pending" && status !== "Validated";
    };

    const onCellValueChanged = (params) => {
      if (activeSubTab === "Template Library") return;
      const { data } = params;
      const updatedRow = data;
      setProducts((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      setActionQueue((q) => [...q, { action: "update", item: updatedRow }]);
      setIsSaveVisible(true);
    };
    const onCellClicked = (params) => {
      if (activeSubTab === "Template Library") return;
      if (params.data?.validation_status === "Pending" || params.data?.validation_status === "Validated") return;
      const { colDef } = params;
      const updatedRow = { ...params.data, [colDef.field]: !params.value };
      setProducts((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      setActionQueue((q) => [...q, { action: "update", item: updatedRow }]);
      setIsSaveVisible(true);
    };
    return [
      {
        headerName: "Name",
        field: "name",
        cellEditor: "agTextCellEditor",
        cellStyle: {
          fontWeight: 500,
          ...leftAlignColDef.cellStyle,
        },
        ...defaultColDef,
        editable: isEditable,
        onCellValueChanged,
        headerCheckboxSelection: true,
      },
      {
        headerName: "Supplier",
        field: "supplier",
        cellEditor: "agTextCellEditor",
        onCellValueChanged,
        ...defaultColDef,
        ...leftAlignColDef,
        editable: isEditable,
      },
      { headerName: "Year", field: "year", cellEditor: "agTextCellEditor", onCellValueChanged, ...defaultColDef, ...centerAlignColDef, editable: isEditable },
      {
        headerName: "Type",
        field: "type",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: () => ({ values: PRODUCT_TYPES }),
        onCellValueChanged,
        ...defaultColDef,
        ...leftAlignColDef,
        editable: isEditable,
      },
      {
        headerName: "Category",
        field: "category",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: (params) => ({ values: getCategoriesForType(params.data.type) }),
        onCellValueChanged,
        ...defaultColDef,
        ...leftAlignColDef,
        editable: isEditable,
      },
      {
        headerName: "Sub Category",
        field: "sub_category",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: (params) => ({ values: getSubCategoriesForType(params.data.type, params.data.category) }),
        onCellValueChanged,
        ...defaultColDef,
        ...leftAlignColDef,
        editable: isEditable,
      },
      // { headerName: "Quantity", field: "quantity", ...rightAlignColDef, ...defaultColDef, hide: isHidden("quantity") },
      { 
        headerName: "GWP (kg CO₂e)",
        field: "gwp",
        colId: "gwp",
        valueGetter: (params) => {
          if (!params.data) return 0;
          const productGwpRows = cachedGwpByEnvType.filter((g) => g.product_id === params.data.id);
          if (productGwpRows.length === 0) return params.data.gwp || 0;
          return productGwpRows.reduce((sum, r) => sum + (Number(r.gwp_sum) || 0), 0);
        },
        ...rightAlignColDef, 
        ...defaultColDef, 
        ...floatFormater 
      },
      {
        headerName: "Total Mass (kg)",
        field: "total_mass",
        colId: "total_mass",
        ...rightAlignColDef,
        ...defaultColDef,
        ...floatFormater,
        editable: false,
      },
      { headerName: "Biogenic GWP (kg CO₂e)", field: "biogenic_gwp", ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { headerName: "Cost ($)", field: "cost", cellEditor: "agTextCellEditor", onCellValueChanged, ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: isEditable },
      { 
        headerName: "Cost per kg CO₂e ($/kg)",
        field: "cost_per_gwp",
        colId: "cost_per_gwp",
        valueGetter: (params) => {
          if (!params.data) return null;
          const productGwpRows = cachedGwpByEnvType.filter((g) => g.product_id === params.data.id);
          const liveGwp = productGwpRows.length > 0 
            ? productGwpRows.reduce((sum, r) => sum + (Number(r.gwp_sum) || 0), 0)
            : Number(params.data.gwp || 0);
          if (liveGwp === 0) return null;
          return Number(params.data.cost || 0) / liveGwp;
        },
        ...rightAlignColDef, 
        ...defaultColDef, 
        ...floatFormater,
        editable: false
      },
      { headerName: "Reused GWP (kg CO₂e)", field: "reused_gwp", ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { headerName: "Circularity Index", field: "mci", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: false },
      {
        headerName: "GWP of Reuse vs Virgin (% of virgin)",
        field: "gwp_reuse_vs_virgin",
        colId: "gwp_reuse_vs_virgin",
        valueGetter: (params) => {
          if (!params.data) return null;
          const reusedGwp = Number(params.data.reused_gwp || params.data.reuse_gwp || 0);
          const stageData = cachedGwpByStage.find((g) => g.product_id === params.data.id);
          const getGwp = () => {
            const productGwpRows = cachedGwpByEnvType.filter((g) => g.product_id === params.data.id);
            if (productGwpRows.length === 0) return params.data.gwp || 0;
            return productGwpRows.reduce((sum, r) => sum + (Number(r.gwp_sum) || 0), 0);
          };
          const emissionA = stageData ? (Number(stageData.A1_A3 || 0) + Number(stageData.A4_A5 || 0)) : getGwp();
          if (reusedGwp === 0 && emissionA === 0) return 0;
          return (reusedGwp / (emissionA || 1)) * 100;
        },
        headerTooltip: "Compared to virgin material",
        ...rightAlignColDef,
        ...defaultColDef,
        ...floatFormater,
        editable: false,
      },
      { headerName: "AP (kg SO₂e)", field: "ap", ...rightAlignColDef, ...defaultColDef, ...exactFloatFormater },
      { headerName: "POCP (kg C₂H₄e)", field: "pocp", ...rightAlignColDef, ...defaultColDef, ...exactFloatFormater },
      { headerName: "ODP (kg CFC-11e)", field: "odp", ...rightAlignColDef, ...defaultColDef, ...exactFloatFormater },
      { headerName: "EP-marine (kg N)", field: "ep_marine", ...rightAlignColDef, ...defaultColDef, ...exactFloatFormater },
      ...(isAdmin
        ? [
          {
            headerName: "Publish Template",
            field: "is_published_template",
            ...checkBoxColDef,
            minWidth: 110,
            width: 110,
            onCellClicked: (params) => {
              if (activeSubTab === "Template Library") return;
              if (params.data?.validation_status === "Pending" || params.data?.validation_status === "Validated") return;
              onCellClicked(params);
            },
            headerComponent: ToggleAll,
            headerComponentParams: { toggle: toggleAllPublishedTemplates },
            editable: isEditable,
            hide: activeSubTab === "Template Library",
          },
        ]
        : []),

      { headerName: "Validation Status", field: "validation_status", ...defaultColDef, ...leftAlignColDef },
      { headerName: "Date Modified", field: "date_modified", ...defaultColDef, ...leftAlignColDef },
    ].map((col) => ({ ...col, hide: isHidden(col.field) }));
  }, [columnVisibilities, colVisibility_key, envFactorOptions, setIsSaveVisible, isAdmin, toggleAllPublishedTemplates, cachedGwpByEnvType, activeSubTab]);
  const defaultCols = PRODUCT_DEFAULT_COLUMNS;

  const memoizedFooterLeftContent = useMemo(() => (
    <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
      <CheckBox
        checked={globalView}
        onChange={(e) => setGlobalView(e.target.checked)}
        label="Global View"
      />
    </Box>
  ), [globalView]);

  return (
    <MainContainer>

      <Box sx={{ mb: 4 }}>
        <Box className="eng-flex-grid">
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Active Products")}
              value={selectedProductIds.length > 0 ? selectedProductIds.length : products.length}
              icon={FiLayers}
              accent="#6366f1"
              bg="rgba(99, 102, 241, 0.12)"
              type="chain"
              delay={0.05}
            />
          </Box>
          {METRIC_CONFIG.map((m, i) => {
            const { key: metricKey, title, ...otherProps } = m;
            return (
              <Box className="eng-metric-item" key={metricKey}>
                <MetricCard
                  {...otherProps}
                  title={t(title)}
                  value={gwpCardProps[metricKey] || 0}
                  forceTons={anyMetricReachesTons}
                  delay={i * 0.07}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontFamily: "Poppins", fontSize: "20px", fontWeight: 500, color: isDark ? "#fff" : "#0f172a", mb: 2, textAlign: 'left' }}>
          {t("Life Cycle Stages")}
        </Typography>
        <Box className="eng-flex-grid">
          {STAGE_CONFIG.map((s, i) => {
            const { key: stageKey, title, subtitle, ...otherProps } = s;
            return (
              <Box className="eng-metric-item" key={stageKey}>
                <LifecycleStageCards
                  {...otherProps}
                  title={t(title)}
                  subtitle={t(subtitle)}
                  variant="stage"
                  value={stageMetrics[stageKey] || 0}
                  forceTons={anyStageReachesTons}
                  delay={(i + 4) * 0.07}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* TABS SWITCHER - Located directly below Lifecycle stages at 100% width */}
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
        <Box sx={{ width: "100%" }}>
          <SegmentedTabs
            options={[
              { label: t("Dashboard"), value: "Dashboard" },
              { label: t("Template Library"), value: "Template Library" },
              { label: t("Validations"), value: "Validations" },
            ]}
            selected={activeSubTab}
            setSelected={setActiveSubTab}
          />
        </Box>
      </Box>

      {/* VIEW 1: DASHBOARD */}
      {activeSubTab === "Dashboard" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column" }}>
          <Paper variant="Table Container" sx={{ mt: tok.spacing.pageGap }} className="stagger-load-item stagger-delay-6">
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {t("Products")}
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
                <Box sx={{ width: 450 }}>
                  <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search for products...")} width={450} />
                </Box>

                <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
                <AddButton
                  onClick={() => {
                    newProduct();
                  }}
                />
                <ExportAsXLSXButton data={products} label="Export as .xlsx" filename="products.xlsx" />
                <HideButton onClick={() => hideProducts(derivedSelectedProducts)} disabled={derivedSelectedProducts.length === 0} />
                <UnhideButton onClick={() => setUnhideDialogOpen(true)} hiddenCount={hiddenProducts.length} />
                <RefreshButton setRefresh={setRefresh} label="Refresh table" />

                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', height: '40px' }}>
                  <Button 
                    onClick={() => setDashboardView("Grid View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: dashboardView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: dashboardView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setDashboardView("Card View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: dashboardView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: dashboardView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>
            <Box>
              {dashboardView === "Card View" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: tok.spacing?.sectionGap || 3, p: 3 }}>
                  {products
                    .filter((p) => (p.name || "").toLowerCase().includes(searchText.toLowerCase()))
                    .map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        resolvedImage={productImageCache[product.id]}
                        isDark={isDark}
                        tok={tok}
                        onClick={() => openProductWorkspace(product)}
                        onAction={() => openProductWorkspace(product)}
                        actionLabel={t("Open Workspace")}
                        onMenuClick={handleCardMenuClick}
                      />
                    ))}
                </Box>
              ) : (
                <AgGrid
                  ref={gridRef}
                  columnDefs={colDefs}
                  rowData={products}
                  onSelectionChanged={onSelectionChanged}
                  handleContextMenu={handleContextMenu}
                  colVisibility_key={colVisibility_key}
                  footerLeftContent={memoizedFooterLeftContent}
                />
              )}
            </Box>

          </Paper>
          <Box sx={{ display: globalView ? "block" : "none", paddingY: 2 }}>
            <DashboardGlobalFootprint 
              items={derivedSelectedProducts.length > 0 ? derivedSelectedProducts : products} 
              type="product" 
            />
          </Box>
        </Box>
      )}

      {/* VIEW 2: TEMPLATE LIBRARY */}
      {activeSubTab === "Template Library" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", mt: 1 }}>
          <Paper variant="Table Container" sx={{ mt: 1 }}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {t("Template Library")} ({templates.length})
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer>
                <Box sx={{ width: 450 }}>
                  <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search templates...")} width={450} />
                </Box>
                
                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', height: '40px' }}>
                  <Button 
                    onClick={() => setTemplateView("Grid View")} 
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: templateView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: templateView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setTemplateView("Card View")} 
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: templateView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: templateView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>

            <Box sx={{ position: 'relative', p: templateView === "Card View" ? 3 : 0 }}>
              {templateView === "Card View" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: tok.spacing?.sectionGap || 3 }}>
                  {templates
                    .filter((t) => (t.name || "").toLowerCase().includes(searchText.toLowerCase()))
                    .map((template) => (
                      <ProductCard
                        key={template.id}
                        product={template}
                        resolvedImage={productImageCache[template.id]}
                        onAction={handleUseTemplate}
                        actionLabel={t("Use Template")}
                        isTemplate={true}
                        isDark={isDark}
                        tok={tok}
                        isSelected={selectedTemplateId === template.id}
                        onClick={() => setSelectedTemplateId((prev) => (prev === template.id ? null : template.id))}
                      />
                    ))}
                </Box>
              ) : (
                <AgGrid
                  columnDefs={colDefs}
                  rowData={templates.filter((t) => (t.name || "").toLowerCase().includes(searchText.toLowerCase()))}
                  onSelectionChanged={() => {}}
                  handleContextMenu={() => {}}
                  colVisibility_key="product_template_library_column_visibility"
                />
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* VIEW 3: VALIDATIONS */}
      {activeSubTab === "Validations" && (
        <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", mt: 1 }}>
          <Paper variant="Table Container" sx={{ mt: 1 }}>
            <TableHeaderContainer>
              <TableTitleContainer>
                <Typography variant="Table Title">
                  {validationSubTab === "Pending" ? t("Pending Validations") : t("Validated Products")}
                </Typography>
              </TableTitleContainer>
              <TableHeaderToolsContainer>
                <Box sx={{ width: 350 }}>
                  <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search validations...")} width={350} />
                </Box>
                
                {/* Validation Status Dropdown */}
                <FormControl size="small" sx={{ minWidth: 140, ml: 1.5 }}>
                  <Select
                    value={validationSubTab}
                    onChange={(e) => setValidationSubTab(e.target.value)}
                    sx={{
                      height: '40px',
                      borderRadius: '8px',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      color: '#659d33',
                      bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'transparent',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: tok.border.divider,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#659d33',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#659d33',
                      }
                    }}
                  >
                    <MenuItem value="Pending" sx={{ fontFamily: 'Poppins', fontSize: '13px', fontWeight: 500 }}>{t("Pending")}</MenuItem>
                    <MenuItem value="Validated" sx={{ fontFamily: 'Poppins', fontSize: '13px', fontWeight: 500 }}>{t("Validated")}</MenuItem>
                  </Select>
                </FormControl>
                
                {/* View Switcher Toggle */}
                <Box sx={{ display: 'flex', border: '1px solid', borderColor: tok.border.divider, borderRadius: '8px', overflow: 'hidden', ml: 1.5, height: '40px' }}>
                  <Button 
                    onClick={() => setValidationView("Grid View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      bgcolor: validationView === "Grid View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: validationView === "Grid View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Grid View")}
                  </Button>
                  <Button 
                    onClick={() => setValidationView("Card View")}
                    sx={{ 
                      borderRadius: 0, 
                      textTransform: 'none',
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '13px',
                      px: 2,
                      borderLeft: '1px solid',
                      borderColor: tok.border.divider,
                      bgcolor: validationView === "Card View" ? "rgba(101, 157, 51, 0.12)" : "transparent",
                      color: validationView === "Card View" ? "#659d33" : (isDark ? "#94a3b8" : "#64748b"),
                      '&:hover': { bgcolor: "rgba(101, 157, 51, 0.2)" }
                    }}
                  >
                    {t("Card View")}
                  </Button>
                </Box>
              </TableHeaderToolsContainer>
            </TableHeaderContainer>

            <Box sx={{ position: 'relative', p: validationView === "Card View" ? 3 : 0 }}>
              {validationView === "Grid View" ? (
                <AgGrid
                  columnDefs={colDefs}
                  rowData={products.filter((p) => {
                    const status = p.validation_status || "Not Validated";
                    const matchesStatus = validationSubTab === "Validated"
                      ? (status === "Validated" || status === "approved")
                      : status === validationSubTab;
                    return matchesStatus && (p.name || "").toLowerCase().includes(searchText.toLowerCase());
                  })}
                  onSelectionChanged={onSelectionChanged}
                  handleContextMenu={handleContextMenu}
                  colVisibility_key="product_validation_column_visibility"
                />
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: tok.spacing?.sectionGap || 3 }}>
                  {products
                    .filter((p) => {
                      const status = p.validation_status || "Not Validated";
                      const matchesStatus = validationSubTab === "Validated"
                        ? (status === "Validated" || status === "approved")
                        : status === validationSubTab;
                      return matchesStatus && (p.name || "").toLowerCase().includes(searchText.toLowerCase());
                    })
                    .map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        resolvedImage={productImageCache[product.id]}
                        onClick={() => openProductWorkspace(product)}
                        isDark={isDark}
                        tok={tok}
                        onMenuClick={(e) => handleCardMenuClick(e, product)}
                      />
                    ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />
      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={derivedSelectedProducts} options={contextMenuOptions} />
      <DialogDelete selectedItems={derivedSelectedProducts} handleDelete={deleteProduct} ref={dialogRef} />
      <UnhideDialog open={unhideDialogOpen} onClose={() => setUnhideDialogOpen(false)} hiddenItems={hiddenProducts} onUnhide={unhideProducts} entityType="Products" />
      <SaveButton />
    </MainContainer>
  );
};

export default ProductDashboard;
